import { useCallback, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest } from '../authConfig';
import { requestUploadSas, uploadFileToBlob } from '../api';

type Status =
  | { kind: 'idle' }
  | { kind: 'uploading'; loaded: number; total: number }
  | { kind: 'success'; blobName: string }
  | { kind: 'error'; message: string };

export function UploadForm() {
  const { instance, accounts } = useMsal();
  const [packageId, setPackageId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getToken = useCallback(async (): Promise<string> => {
    const account = accounts[0];
    if (!account) throw new Error('No signed-in account');
    try {
      const result = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...tokenRequest, account });
      }
      throw err;
    }
  }, [instance, accounts]);

  const validPackageId = /^[A-Za-z0-9._-]{1,64}$/.test(packageId);
  const canSubmit = validPackageId && !!file && status.kind !== 'uploading';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !file) return;

    setStatus({ kind: 'uploading', loaded: 0, total: file.size });
    try {
      const token = await getToken();
      const ticket = await requestUploadSas(
        token,
        packageId,
        file.name,
        file.type || 'application/octet-stream',
        overwrite,
      );
      await uploadFileToBlob(ticket, file, (loaded, total) =>
        setStatus({ kind: 'uploading', loaded, total }),
      );
      setStatus({ kind: 'success', blobName: ticket.blobName });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setStatus({ kind: 'error', message: (err as Error).message });
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
  }

  return (
    <form className="upload-form" onSubmit={onSubmit}>
      <label>
        Package ID
        <input
          type="text"
          value={packageId}
          onChange={(e) => setPackageId(e.target.value)}
          placeholder="e.g. RLT-20261006-WF-847291"
          required
          maxLength={64}
          aria-invalid={packageId.length > 0 && !validPackageId}
        />
        {packageId.length > 0 && !validPackageId && (
          <small className="hint error">
            1–64 chars of letters, digits, dot, dash, underscore only.
          </small>
        )}
      </label>

      <div
        className={`drop-zone${file ? ' has-file' : ''}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file ? (
          <div>
            <strong>{file.name}</strong>
            <div><small>{formatBytes(file.size)} · {file.type || 'unknown type'}</small></div>
          </div>
        ) : (
          <div>Drop a file here or click to browse</div>
        )}
      </div>

      <label className="checkbox-row">
        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
        Replace existing file with the same name in this package
      </label>

      <button type="submit" disabled={!canSubmit}>
        {status.kind === 'uploading' ? 'Uploading…' : 'Upload'}
      </button>

      {status.kind === 'uploading' && (
        <div className="progress">
          <progress value={status.loaded} max={status.total} />
          <span>{formatBytes(status.loaded)} / {formatBytes(status.total)}</span>
        </div>
      )}
      {status.kind === 'success' && (
        <div className="status success">
          Uploaded successfully as <code>{status.blobName}</code>
        </div>
      )}
      {status.kind === 'error' && (
        <div className="status error">Error: {status.message}</div>
      )}
    </form>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
